import { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, useLocation, Link, useSearchParams, Navigate } from 'react-router-dom';
import {
  Menu,
  X,
  Home,
  ShieldAlert,
  MapPin,
  UserRound,
  ArrowRightFromLine,
  ArrowRightToLine,
} from 'lucide-react';
import logoFull from '../../assets/images/logo.svg';
import logoMark from '../../assets/images/logo-mark.svg';
import { ImpersonationProfileProvider, useTenantProfile } from '../../contexts/TenantProfileContext';
import { ProspectPageProgressProvider, usePageProgressByHref } from '../../contexts/ProspectPageProgressContext';
import { PROSPECT_PORTAL_STEPS } from '../../constants/prospectPortalSteps';
import { logicalPortalPath, portalHref } from '../../utils/portalPaths';
import SidebarProgressRing from './SidebarProgressRing';
import ProspectSidebarProgress from './ProspectSidebarProgress';
import ProspectNextSectionButton from './ProspectNextSectionButton';
import { TenantAppearanceToggle } from './TenantAppearanceToggle';
import { TenantAppearanceDock } from './TenantAppearanceDock';

interface NavItem {
  name: string;
  href: string;
  icon: typeof Home;
  stepLabel?: string;
}

function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

function SidebarContent({
  currentPath,
  onNavClick,
  themeOptions = 'full',
}: {
  currentPath: string;
  onNavClick?: () => void;
  themeOptions?: 'light-dark' | 'full';
}) {
  const { tenant, prospect, applicant, kind } = useTenantProfile();
  const pageProgressByHref = usePageProgressByHref();
  const logicalCurrent = logicalPortalPath(currentPath);

  const displayName = tenant
    ? [tenant.firstName, tenant.lastName].filter(Boolean).join(' ') || 'Tenant'
    : prospect?.displayName || applicant?.displayName || null;

  const residentialLease = tenant?.residentialLease;
  const propertyLabel = residentialLease?.propertyName
    ? residentialLease.propertyUnitCount > 1 && residentialLease.unitNumber
      ? `#${residentialLease.unitNumber} - ${residentialLease.propertyName}`
      : residentialLease.propertyName
    : null;

  const navigation = useMemo<NavItem[]>(() => {
    if (kind === 'prospect') {
      return PROSPECT_PORTAL_STEPS.map(({ name, href, icon, stepLabel }) => ({
        name,
        href: portalHref(href, true),
        icon,
        stepLabel,
      }));
    }
    const items: NavItem[] = [
      { name: 'My Information', href: portalHref('/my-information', true), icon: UserRound },
    ];
    if (tenant?.moveIn != null) {
      items.push({ name: 'Move In', href: portalHref('/move-in', true), icon: ArrowRightToLine });
    }
    if (tenant?.moveOut != null) {
      items.push({ name: 'Move Out', href: portalHref('/move-out', true), icon: ArrowRightFromLine });
    }
    return items;
  }, [kind, tenant?.moveOut, tenant?.moveIn]);

  return (
    <>
      <div className="flex h-16 shrink-0 items-center px-2">
        <Link to="/impersonate" onClick={onNavClick}>
          <img src={logoFull} alt="Amber Peak" className="h-7 w-auto" />
        </Link>
      </div>

      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            {kind === 'prospect' && (
              <p className="mb-2 px-2 text-xs font-medium text-gray-400 dark:text-gray-500">Prospect portal</p>
            )}
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map((item) => {
                const isCurrent = logicalCurrent === logicalPortalPath(item.href);
                const Icon = item.icon;
                const progress = kind === 'prospect' ? pageProgressByHref[logicalPortalPath(item.href)] ?? null : null;
                const activeStyles =
                  kind === 'prospect'
                    ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400'
                    : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400';
                const idleStyles =
                  kind === 'prospect'
                    ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:text-red-700 dark:hover:bg-white/5 dark:hover:text-red-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:text-amber-700 dark:hover:bg-white/5 dark:hover:text-amber-400';
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={onNavClick}
                      className={cn(
                        isCurrent ? activeStyles : idleStyles,
                        'group flex items-center gap-3 rounded-md px-2 py-2.5 text-sm font-semibold',
                      )}
                    >
                      {progress ? (
                        <SidebarProgressRing completed={progress.completed} total={progress.total}>
                          <Icon
                            size={18}
                            className={cn(
                              isCurrent ? 'text-red-700 dark:text-red-400' : 'text-gray-400 group-hover:text-red-700',
                            )}
                          />
                        </SidebarProgressRing>
                      ) : (
                        <Icon
                          aria-hidden="true"
                          size={22}
                          className={cn(
                            isCurrent ? (kind === 'prospect' ? 'text-red-700' : 'text-amber-700') : 'text-gray-400',
                            'shrink-0',
                          )}
                        />
                      )}
                      <span className="min-w-0 flex flex-1 flex-col justify-center">
                        {item.stepLabel ? (
                          <span className="text-[11px] font-medium uppercase tracking-wide leading-4 text-gray-400">
                            {item.stepLabel}
                          </span>
                        ) : null}
                        <span className="leading-5">{item.name}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          <li className="-mx-6 mt-auto">
            <TenantAppearanceToggle themeOptions={themeOptions} />
            <div className="border-t border-gray-100 px-6 py-4 dark:border-white/5">
              {displayName && (
                <p className="text-sm font-semibold text-gray-900 truncate dark:text-white">{displayName}</p>
              )}
              {propertyLabel && (
                <div className="flex items-center gap-1.5 mt-1">
                  <MapPin size={13} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  <span className="text-xs text-gray-500 truncate dark:text-gray-400">{propertyLabel}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 mt-3">
                <ShieldAlert size={14} className="text-amber-600 shrink-0 dark:text-amber-400" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Admin View</span>
              </div>
            </div>
          </li>
        </ul>
      </nav>
    </>
  );
}

function ImpersonationBanner() {
  const { tenant, prospect, applicant, kind } = useTenantProfile();
  const name =
    kind === 'tenant' && tenant
      ? [tenant.firstName, tenant.lastName].filter(Boolean).join(' ')
      : prospect?.displayName || applicant?.displayName || 'Loading...';

  return (
    <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm font-semibold flex items-center justify-center gap-2">
      <ShieldAlert size={16} />
      <span>Admin Impersonation — Viewing as {name}</span>
      <button
        onClick={() => window.close()}
        className="ml-4 px-3 py-0.5 rounded bg-amber-600 hover:bg-amber-700 text-xs font-medium transition-colors"
      >
        Close Tab
      </button>
    </div>
  );
}

function LayoutContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { loading, error, kind } = useTenantProfile();
  const currentProspectStep =
    kind === 'prospect'
      ? PROSPECT_PORTAL_STEPS.find((step) => step.href === logicalPortalPath(location.pathname))
      : undefined;

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeSidebar();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen, closeSidebar]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <TenantAppearanceDock />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <TenantAppearanceDock />
        <div className="text-center max-w-md">
          <ShieldAlert size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Impersonation Failed</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.close()}
            className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
          >
            Close Tab
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <ImpersonationBanner />
      {kind === 'prospect' ? <ProspectSidebarProgress /> : null}

      <div className="flex-1 flex overflow-hidden">
        {/* Mobile backdrop */}
        <div
          className={cn(
            'fixed inset-0 z-40 bg-gray-900/80 transition-opacity duration-300 lg:hidden',
            sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
          aria-hidden="true"
          onClick={closeSidebar}
        />

        {/* Mobile drawer */}
        <div
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out lg:hidden',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex h-full grow flex-col gap-y-5 overflow-y-auto bg-white dark:bg-gray-800 px-6 pb-2 shadow-2xl dark:bg-gray-900">
            <SidebarContent
              currentPath={location.pathname}
              onNavClick={closeSidebar}
              themeOptions="light-dark"
            />
          </div>
          <button
            type="button"
            onClick={closeSidebar}
            className={cn(
              'absolute left-full top-0 flex w-16 justify-center pt-5 transition-opacity duration-300',
              sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
          >
            <span className="sr-only">Close sidebar</span>
            <X size={24} className="text-white" />
          </button>
        </div>

        {/* Desktop sidebar */}
        <div className="hidden lg:flex lg:w-72 lg:flex-col lg:shrink-0">
          <div className="relative flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 dark:border-white/10 dark:bg-gray-900">
            <SidebarContent currentPath={location.pathname} />
          </div>
        </div>

        {/* Mobile top bar */}
        <div className="sticky top-0 z-40 shrink-0 bg-amber-600 shadow-sm lg:hidden">
          <div className="flex min-h-[3.25rem] items-center gap-x-4 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:gap-x-6 sm:px-6 sm:pb-4">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="touch-optimized flex shrink-0 items-center justify-center rounded-md p-2 text-white hover:bg-white/10 hover:text-amber-100"
            >
              <span className="sr-only">Open sidebar</span>
              <Menu size={24} />
            </button>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <img src={logoMark} alt="Amber Peak" className="h-7 w-auto shrink-0 brightness-0 invert" />
              {currentProspectStep ? (
                <div className="min-w-0 flex-1 text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-100">
                    {currentProspectStep.stepLabel}
                  </p>
                  <p className="truncate text-sm font-semibold leading-tight text-white">{currentProspectStep.name}</p>
                </div>
              ) : (
                <span className="truncate text-xs font-medium text-white/80">Admin View</span>
              )}
            </div>
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-gray-100 dark:bg-gray-900">
          <div className={kind === 'prospect' ? 'py-6 pb-32 sm:pb-36' : 'py-6'}>
            <div className="px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {kind === 'prospect' ? <ProspectNextSectionButton currentPath={location.pathname} /> : null}
    </div>
  );
}

const SESSION_KEY = 'impersonation_token';

/**
 * Entry point for the /impersonate route tree.
 * A URL token always wins — if a new ?token=xxx arrives it replaces any
 * previous session, forcing a full re-mount of the provider tree so the
 * old tenant's data is flushed and the new tenant's profile loads fresh.
 * The token is stashed in sessionStorage so in-app navigation
 * (e.g. /impersonate → /impersonate/move-out) keeps working.
 * Closing the tab wipes the token automatically.
 */
export default function ImpersonationLayout() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlToken = searchParams.get('token');
  const [token, setToken] = useState<string | null>(() => {
    if (urlToken) {
      sessionStorage.setItem(SESSION_KEY, urlToken);
      return urlToken;
    }
    return sessionStorage.getItem(SESSION_KEY);
  });

  useEffect(() => {
    if (urlToken) {
      sessionStorage.setItem(SESSION_KEY, urlToken);
      setToken(urlToken);
      searchParams.delete('token');
      setSearchParams(searchParams, { replace: true });
    }
  }, [urlToken, searchParams, setSearchParams]);

  if (!token) {
    return <Navigate to="/" replace />;
  }

  return (
    <ImpersonationProfileProvider key={token} token={token}>
      <ProspectPageProgressProvider>
        <LayoutContent />
      </ProspectPageProgressProvider>
    </ImpersonationProfileProvider>
  );
}
